import useChartOptions from '@/features/data-viewer/hooks/useChartOptions';
import useDateRange from '@/features/data-viewer/hooks/useDateRange';
import useLiveMode from '@/features/data-viewer/hooks/useLiveMode';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsStock from 'highcharts/modules/stock';
import React, { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { darkTheme, lightTheme } from '@/components/highcharts/themes';
HighchartsStock(Highcharts);

/**
 * Chart component for Data Viewer.
 * @param {Object} props
 * @param {Object} props.options - Highcharts options
 * @param {React.RefObject} props.chartRef - Ref for the chart instance
 */
const DataViewerChart = ({
  isLoading,
  chartSeries,
  yAxes,
  seriesVisibility,
  setSeriesVisibility,
}) => {
  const chartRef = React.useRef(null);
  const theme = useTheme();
  const [dateRange] = useDateRange();
  const [liveMode] = useLiveMode();

  const chartOptions = useChartOptions({
    chartSeries,
    liveMode,
    yAxes,
    seriesVisibility,
    setSeriesVisibility,
    dateRange,
  });

  // Set navigator to full range when dateRange changes
  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return;
    const chart = chartRef.current && chartRef.current.chart;
    if (chart && chart.xAxis && chart.xAxis[0]) {
      const min = dateRange.from.toMillis();
      const max = dateRange.to.toMillis();
      chart.xAxis[0].setExtremes(min, max, true, false);
    }
  }, [chartRef, dateRange.from, dateRange.to]);

  useEffect(() => {
    if (!chartRef.current || liveMode) return;
    const chart = chartRef.current.chart;
    if (isLoading) {
      chart.showLoading();
    } else {
      chart.hideLoading();
    }
  }, [chartRef, isLoading, liveMode]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartRef.current?.chart) {
      const chart = chartRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: {
              style: { color: themeOptions.xAxis.labels.style.color },
            },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  return (
    <HighchartsReact
      containerProps={{ style: { height: '600px', width: '100%' } }}
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartRef}
    />
  );
};

export default DataViewerChart;
